import { useMutation, useQuery } from "@apollo/client";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import * as Yup from "yup";

import routes from "./routes";
import AuthRoute from "../../../../components/AuthRoute";
import { FormSaveStatus, NavBar, Spinner } from "../../../../components/ui";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { getChangedFields, Portal } from "../../../../util";
import { FETCH_JOB_REQUEST as fetchJob } from "../../../../graphql/query/Job";
import { UPDATE_JOB_REQUEST as updateJobMutation } from "../../../../graphql/mutation/Job";

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...remainingProps} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const JobForm = props => {
  const { id } = useParams();
  const { data: { job } = {}, loading } = useQuery(
    fetchJob({
      jobStatus: true,
      jobType: true,
      costOwner: true,
      requestUser: true,
      user: true,
      artworkDescriptions: true,
      pickupGroup: true,
      jobItems: {
        instance: {
          status: {},
          condition: {},
          warehouseItem: {},
        },
      },
    }),
    {
      variables: { id: +id },
    },
  );

  const [updateJob] = useMutation(updateJobMutation());

  function isValidDate(d) {
    return d instanceof Date && !isNaN(d);
  }

  const formik = useAutosaveFormik({
    initialValues: job,
    validationSchema: Yup.object({
      expenses: Yup.number().positive().nullable(),
      hours: Yup.number().positive().nullable(),
      hoursvan: Yup.number().positive().nullable(),
      overtimehours: Yup.number().positive().nullable(),
      permanentHours: Yup.number().positive().nullable(),
      rate: Yup.number().positive().nullable(),
      rateOvertime: Yup.number().positive().nullable(),
      rateStaff: Yup.number().positive().nullable(),
      rateVan: Yup.number().positive().nullable(),
      technicians: Yup.number().positive().integer().nullable(),
      duetime: Yup.string()
        .required("Due-Time must be greater than or equal to 10 AM GMT")
        .test(
          "validation",
          `Due-Time must be greater than or equal to 10 AM GMT`,
          function (value) {
            const newDueTime = new Date(value);
            if (isValidDate(newDueTime)) {
              return newDueTime.getHours() >= 10;
            } else if (typeof value === "string") {
              return value.split(":")[0] >= 10;
            }
            return false;
          },
        ),
    }),
    onSave: async (values, savedValues) => {
      const changedFields = getChangedFields(values, savedValues);

      if (Object.keys(changedFields).length > 0) {
        if (changedFields?.dueDate) {
          const dueDate = new Date(`${values?.dueDate}`);
          changedFields.dueDate = dueDate;
        }

        if (changedFields?.duetime) {
          const timeString = values?.duetime;
          const [hours, minutes] = timeString.split(":").map(Number);
          const dueDate = values?.dueDate
            ? new Date(values?.dueDate)
            : new Date("1899-12-30");
          const dueDateTime = new Date(
            dueDate.getFullYear(),
            dueDate.getMonth(),
            dueDate.getDate(),
            hours,
            minutes,
          );

          changedFields.duetime = dueDateTime;
        }

        const { data } = await updateJob({
          variables: {
            input: {
              id: job?.id,
              ...changedFields,
            },
          },
        });

        if (!data?.updateJob?.success) {
          throw new Error(
            data?.updateJob?.error || "The job could not be saved",
          );
        }
      }
    },
  });

  const bodyProps = {
    ...props,
    formik,
    job,
    jobId: +id,
  };

  return (
    <div className="relative flex min-h-full">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="flex min-h-full min-w-0 flex-1 flex-col overflow-x-auto px-4 py-4 sm:px-6 lg:p-8">
        <Body loading={loading} {...bodyProps} />
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <JobForm key={id} {...props} />;
};

export default Wizard;
